import { useLayoutEffect, useRef, useState } from "react";

// USAGE:
// const { scrollRef, mirrorRef, onScrollRef, onScrollMirror, needsScroll, scrollWidth } =
//   useMirrorScrollbar(deps);
//
// <div className="my-scroll" ref={scrollRef} onScroll={onScrollRef}>
//   <table>...</table>
// </div>
// {needsScroll && (
//   <div className="my-mirror-scroll" ref={mirrorRef} onScroll={onScrollMirror}>
//     <div style={{ width: scrollWidth, height: 1 }} />
//   </div>
// )}
//
// The real element's own scrollbar should be hidden in CSS
// (scrollbar-width: none + ::-webkit-scrollbar{display:none}) so only the
// mirror strip is ever visible — otherwise both show at once whenever the
// element is short enough that its native scrollbar sits in view already.
//
// A second, thin horizontal-scrollbar strip that stays pinned to the bottom
// of the viewport (via CSS `position: sticky; bottom: 0` on the mirror
// element) while a wide, natively-scrolling element is in view — so its
// scrollbar is always reachable near the top of the page, instead of
// wherever the real element's bottom edge happens to land (which, for a tall
// element, can be far below the fold). `deps` re-measures needsScroll/
// scrollWidth whenever content that could change the real element's
// scrollWidth changes (e.g. row count, visible columns) — same idea as a
// useEffect dependency array.
export function useMirrorScrollbar(deps = []) {
  const scrollRef = useRef(null);
  const mirrorRef = useRef(null);
  // Guards against the two onScroll handlers re-triggering each other
  // (mirror's sync sets scrollLeft on the real element, which fires ITS
  // onScroll, which would otherwise sync back into the mirror, forever).
  const syncing = useRef(false);

  const [needsScroll, setNeedsScroll] = useState(false);
  const [scrollWidth, setScrollWidth] = useState(0);

  /**
   * Sync the scrollLeft of the source element to the target element.
   * @param {*} source 
   * @param {*} target 
   * @returns 
   */
  const syncFrom = (source, target) => {
    if (!target || syncing.current) return;
    syncing.current = true;
    target.scrollLeft = source.scrollLeft;
    syncing.current = false;
  };

  /**
   * Update the needsScroll and scrollWidth states when the scrollRef element changes.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const update = () => {
      setNeedsScroll(el.scrollWidth > el.clientWidth);
      setScrollWidth(el.scrollWidth);
    };
    update();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    scrollRef,
    mirrorRef,
    needsScroll,
    scrollWidth,
    onScrollRef: () => syncFrom(scrollRef.current, mirrorRef.current),
    onScrollMirror: () => syncFrom(mirrorRef.current, scrollRef.current),
  };
}

export default useMirrorScrollbar;
